import { fancyAlert } from "components/ui/fancy-alert";
import { fancyChoice } from "components/ui/fancy-choice";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { trpcClient } from "components/rpc/trpc";
import * as React from "react";
import { useDispatch } from "react-redux";
import { loadAllUsers } from "store/actions";
import { ClientIntakeEmailResponse } from "store/actions/email";
import { usePreferences } from "store/selectors/hooks";
import { RequestDescription } from "types/request-description";
import { handleEvent } from "util/user-events";
import { RequestState } from "../../../../constants/loading";

export const BottomPanel = (props: {
    requestDescription: RequestDescription;
    dismiss: () => void;
    sendMessage: () => void;
    newMessage: () => void;
    sendEnabled: boolean;
}): JSX.Element => {
    const { requestDescription } = props;
    const preferences = usePreferences();
    const dispatch = useDispatch();

    const disablePrompt = React.useCallback(async () => {
        await trpcClient.userPreference.setBooleanPreference.mutate({
            field: "promptSendAnotherEmail",
            value: false,
        });
        handleEvent("preference-change", {
            field: "promptSendAnotherEmail",
            value: false,
        });
        await loadAllUsers(dispatch);
    }, [dispatch]);

    React.useEffect(() => {
        (async () => {
            if (
                requestDescription.status === RequestState.DONE ||
                requestDescription.status === RequestState.ERROR
            ) {
                let accepted: string[] = [];
                let rejected: string[] = [];
                let authError = false;

                if (typeof requestDescription.data === "object") {
                    const response = requestDescription.data as ClientIntakeEmailResponse;

                    if (Array.isArray(response.deliveryResults)) {
                        response.deliveryResults.forEach(dr => {
                            if (Array.isArray(dr.accepted)) {
                                accepted = accepted.concat(dr.accepted);
                            }

                            if (Array.isArray(dr.rejected)) {
                                rejected = rejected.concat(dr.rejected);
                            }

                            if (dr.code === "EAUTH") {
                                authError = true;
                            }
                        });
                    }
                }

                if (authError) {
                    await fancyAlert(
                        "Update your email credentials",
                        "Sending this email failed because the configured credentials for your email provider need to be updated. Navigate to your email settings page, reconnect your email provider, and try again.",
                    );
                } else if (rejected.length > 0) {
                    const sendAnother = await fancyConfirm(
                        "Some emails failed to send.",
                        `Mails to the recipients below failed to send, likely due to a formatting issue with the email address.\n\n${rejected.join(
                            "\n",
                        )}\n\nWould you like to go back to the email and try again?`,
                        "Yes",
                        "No",
                    );

                    if (sendAnother) {
                        props.newMessage();
                    } else {
                        props.dismiss();
                    }
                } else if (
                    accepted.length > 0 ||
                    requestDescription.status === RequestState.DONE
                ) {
                    if (!preferences.promptSendAnotherEmail) {
                        handleEvent("email-send-another-prompt", {
                            response: "skipped-disabled",
                        });
                        props.dismiss();
                    } else {
                        const result = await fancyChoice({
                            title: "Mail successfully sent.",
                            prompt: "Would you like to send another email?",
                            actions: [
                                { label: "Yes", value: "yes", style: "primary" },
                                { label: "No", value: "no", style: "secondary" },
                                {
                                    label: "No, and never ask me again",
                                    value: "never",
                                    style: "subtle",
                                },
                            ],
                        });
                        handleEvent("email-send-another-prompt", {
                            response: result,
                        });
                        if (result === "yes") {
                            props.newMessage();
                        } else {
                            if (result === "never") {
                                await disablePrompt();
                            }
                            props.dismiss();
                        }
                    }
                } else if (requestDescription.status === RequestState.ERROR) {
                    await fancyAlert(
                        "Sorry, something went wrong",
                        "Sorry, something went wrong! Please try reloading the page to fix the issue.",
                    );
                }
            }
        })();
    }, [requestDescription.status]); // eslint-disable-line react-hooks/exhaustive-deps
    if (requestDescription.status === RequestState.NOTSTARTED) {
        return (
            <div className="flex items-center justify-end space-x-3">
                <div>
                    <SecondaryButton onClick={props.dismiss}>Discard</SecondaryButton>
                </div>
                <div>
                    <PrimaryButton
                        onClick={props.sendMessage}
                        disabled={!props.sendEnabled}>
                        Send
                    </PrimaryButton>
                </div>
            </div>
        );
    }
    if (requestDescription.status === RequestState.REQUESTED) {
        return (
            <div className="flex items-center justify-center space-x-2">
                <div>
                    <Spinner />
                </div>
                <div className="font-bold">Sending...</div>
            </div>
        );
    }
    if (
        requestDescription.status === RequestState.DONE ||
        requestDescription.status === RequestState.ERROR
    ) {
        return null;
    }
    const exhaustiveCheck: never = requestDescription.status;
    throw new RangeError(`Expected ${exhaustiveCheck} to be matched, but wasn't.`);
};
